import { Suspense, useEffect } from "react";
import { toast } from "saphir";
import { ConfirmDialogProvider } from "sui";

import {
    deferAccessRedirectNotice,
    setResolvedAccessRedirectPathname,
    takeAccessRedirectPending,
    takeResolvedAccessRedirectNotice,
} from "@lib/access/redirectNotice";
import { customerIoIdentify } from "@lib/customerio";
import { i18n } from "@lib/i18n";
import { queries } from "@lib/queries";
import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    createRootRouteWithContext,
    createRoute,
    createRouter,
    Outlet,
    redirect,
    RouterProvider,
    useMatches,
} from "@tanstack/react-router";
import { useUnleashContext } from "@unleash/proxy-client-react";

import OrganisationLayout from "../layouts/OrganisationLayout";

import { activateAccountRoute } from "./ActivateAccount/route";
import {
    contractsBatchesArchivesRoute,
    contractsIndexRoute,
    contractsRoute,
    contractsSignatureRoute,
} from "./Contracts/route";
import ErrorPage from "./ErrorPage/ErrorPage";
import { guestFeedbackRoute } from "./GuestFeedback/route";
import { homeRoute } from "./Home/route";
import { horizonRoute } from "./Horizon/route";
import { invoicesRoute } from "./Invoices/route";
import { jobDescriptionsRoute } from "./JobDescriptions/route";
import { logAsRoute } from "./LogAs/route";
import { networkAttendancesRoute } from "./NetworkAttendances/route";
import { networkTasksRoute } from "./NetworkTasks/route";
import { planningEditionRoute, planningRoute } from "./Planning/route";
import { preselectionRoute } from "./Preselection/route";
import {
    departmentsSettingsRoute,
    legalInformationRoute,
    paymentInformationRoute,
    profileRoute,
    remunerationInformationRoute,
    settingsIndexRoute,
    settingsRoute,
    teamSettingsRoute,
} from "./Settings/route";
import { signInRoute } from "./SignIn/route";
import { signUpRoute } from "./SignUp/route";
import {
    detailsStepRoute,
    flexiblePlanningStepRoute,
    motiveStepRoute,
    planningTypeStepRoute,
    shiftsStepRoute,
    summaryRoute,
    taskFormRoute,
    WorkersSelectionRoute,
} from "./TaskPosting/route";
import {
    tasksListHomeRoute,
    tasksListIndexRoute,
    tasksListStatusRoute,
    tasksRoute,
    tasksRouteLayout,
    taskViewRoute,
} from "./TasksList/route";
import { timesheetsRoute } from "./Timesheets/route";
import { userProfileRoute } from "./UserProfile/route";
import {
    getAccessFallbackRoute,
    getAccessRedirectMessageKey,
    getUser,
    requireConnectedUser,
} from "./authorization";
import NotFound from "./NotFound";

interface RootContext {
    getTitle?: () => string;
    queryClient: QueryClient;
}

export const rootRoute = createRootRouteWithContext<RootContext>()({
    component: () => {
        const matches = useMatches();

        const { data: user } = useQuery(queries.user.detail());

        useEffect(() => {
            function getFirstAvailableTitle() {
                const baseTitle = "Side";

                for (let i = matches.length - 1; i >= 0; i--) {
                    const match = matches[i];
                    if (match.context && "getTitle" in match.context) {
                        try {
                            const title = match.context.getTitle?.();
                            if (title) {
                                document.title = `${baseTitle} · ${title}`;
                                return;
                            }
                        } catch (error) {
                            if (import.meta.env.DEV) {
                                console.warn("Error getting title", match.pathname, error);
                            }
                        }
                    } else if (i === matches.length - 1 && import.meta.env.DEV) {
                        // Warning in dev so we don't forget to add a title
                        console.warn("no getTitle", match.pathname, match);
                    }
                }

                // If no title is found, use the default title
                document.title = baseTitle;
            }

            void getFirstAvailableTitle();
        }, [matches]);

        // unleash context
        const updateContext = useUnleashContext();
        const userId = localStorage.getItem("Meteor.userId") ?? "";
        const organisationId = localStorage.getItem("side_team_activeOrganisationId") ?? "";
        const networkId = localStorage.getItem("side_team_groupId") ?? "";
        // unleash expects a string
        const logAs =
            user?.isInsider && localStorage.getItem("side_team_logasId") ? "true" : "false";

        useEffect(() => {
            updateContext({ userId, properties: { organisationId, networkId, logAs } });
        }, [userId, organisationId, networkId, logAs]);

        useEffect(() => {
            if (!userId || !user) return;
            customerIoIdentify(userId, {
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.role,
                organisationId: user.organisation?.id,
                organisationName: user.organisation?.name,
                isInsider: user.isInsider,
            });
        }, [userId, user]);

        return (
            <>
                <Outlet />
                <Suspense fallback={null}>{/* <TanStackRouterDevtools /> */}</Suspense>
            </>
        );
    },
    notFoundComponent: () => <NotFound />,
});

export const globalLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "globalLayout",
    beforeLoad: requireConnectedUser,
    component: () => (
        <ConfirmDialogProvider
            confirmButtonLabel={i18n.action_confirm()}
            cancelButtonLabel={i18n.action_cancel()}
        >
            <OrganisationLayout>
                <Outlet />
            </OrganisationLayout>
        </ConfirmDialogProvider>
    ),
});
const defaultRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: `/`,
    beforeLoad: async () => {
        const user = await getUser();
        const destination = getAccessFallbackRoute(user);

        if (takeAccessRedirectPending(destination)) {
            const messageKey = getAccessRedirectMessageKey(user, destination);
            deferAccessRedirectNotice(i18n[messageKey](), destination);
        }

        throw redirect({ to: destination });
    },
});

const routeTree = rootRoute.addChildren([
    globalLayoutRoute,
    defaultRoute,
    signInRoute,
    signUpRoute,
    guestFeedbackRoute,
    activateAccountRoute,
    homeRoute,
    logAsRoute,
    preselectionRoute,
    tasksRoute.addChildren([
        tasksRouteLayout.addChildren([
            tasksListIndexRoute,
            tasksListHomeRoute,
            tasksListStatusRoute,
        ]),
        taskViewRoute,
    ]),
    planningRoute,
    planningEditionRoute,
    contractsRoute.addChildren([contractsIndexRoute, contractsBatchesArchivesRoute]),
    contractsSignatureRoute,
    timesheetsRoute,
    networkAttendancesRoute,
    networkTasksRoute,
    invoicesRoute,
    userProfileRoute,
    settingsRoute.addChildren([
        settingsIndexRoute,
        legalInformationRoute,
        paymentInformationRoute,
        remunerationInformationRoute,
        profileRoute,
        teamSettingsRoute,
        departmentsSettingsRoute,
    ]),
    taskFormRoute.addChildren([
        motiveStepRoute,
        planningTypeStepRoute,
        flexiblePlanningStepRoute,
        shiftsStepRoute,
        detailsStepRoute,
        WorkersSelectionRoute,
        summaryRoute,
    ]),
    jobDescriptionsRoute,
    horizonRoute,
]);

export const router = createRouter({
    routeTree,
    defaultErrorComponent: ErrorPage,
    defaultPreload: "intent",
    // Since we're using React Query, we don't want loader calls to ever be stale
    // This will ensure that the loader is always called when the route is preloaded or visited
    defaultPreloadStaleTime: 0,
    context: {
        queryClient: undefined!, // This will be set after we wrap the app in a QueryClientProvider
    },
    scrollRestoration: true,
});

setResolvedAccessRedirectPathname(router.state.location.pathname);
router.subscribe("onResolved", ({ toLocation }) => {
    setResolvedAccessRedirectPathname(toLocation.pathname);
    const notice = takeResolvedAccessRedirectNotice(toLocation.pathname);
    if (notice) toast.info(notice);
});

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

export function AppRouter() {
    const queryClient = useQueryClient();
    return <RouterProvider router={router} context={{ queryClient }} />;
}
